import { FC } from 'react';
import { Box, Divider, Switch, Typography } from '@mui/material';
import { observer } from 'mobx-react-lite';

import { Badge, Tip } from '@components';
import { useDirection } from '@hooks';
import { AssessmentDomainStatus } from '@libs/constants';
import { Trans, useTrans } from '@libs/i18n';
import { FileUploadType } from '@libs/types';
import { NoFlipBox, NoFlipTypography } from '@utils';

import type { ExpectedDocument } from '../../../utils/assessment-mapper';
import { ReassessmentDomainBanner } from '../ReassessmentDomainBanner';

import { DomainField, DomainFieldRenderer } from './DomainFieldRenderer';
import { ExpectedDocumentsDropdown } from './ExpectedDocumentsDropdown';

export interface DomainDetailsProps {
  isRequired: boolean;
  domainName: string;
  description?: string | null;
  fields: DomainField[];
  expectedDocuments?: ExpectedDocument[];
  files?: FileUploadType[];
  onAddFiles?: (files: File[]) => void;
  onDeleteFile?: (fileId: string) => void;
  onCancelUpload?: (fileId: string) => void;
  onUndoDelete?: (fileId: string) => void;
  onRetryDelete?: (fileId: string) => void;
  onRetryUpload?: (fileId: string) => void;
  onRemoveFailedUpload?: (fileId: string) => void;
  disabled?: boolean;
  isApplicable?: boolean;
  onApplicabilityChange?: (isApplicable: boolean) => void;
  domainStatus?: AssessmentDomainStatus;
  noEvidenceChecked?: boolean;
  /** Optional render function for additional content below each file item */
  renderExtraContent?: (file: FileUploadType) => React.ReactNode;
  /** Optional render function for an inline badge in each file's meta row. */
  renderFileBadge?: (file: FileUploadType) => React.ReactNode;
  /** Optional predicate — when true for a file, that card shows a moving overlay. */
  isFileMoving?: (file: FileUploadType) => boolean;
  /** True while a file is still queued for or undergoing AI analysis. */
  isFileProcessing?: (file: FileUploadType) => boolean;
  /** Re-assessment context — when present, renders the per-domain banner. */
  parentVersion?: number;
  preloadedDocumentCount?: number;
  parentDomainScorePercentage?: number | null;
  /**
   * Fired when the user clicks any disabled control on a completed domain —
   * the page surfaces a "click Edit to start" tooltip in response.
   */
  onDisabledAreaClick?: () => void;
}

export const DomainDetails: FC<DomainDetailsProps> = observer(
  ({
    isRequired,
    domainName,
    description,
    fields,
    expectedDocuments = [],
    files = [],
    onAddFiles,
    onDeleteFile,
    onCancelUpload,
    onUndoDelete,
    onRetryDelete,
    onRetryUpload,
    onRemoveFailedUpload,
    disabled = false,
    isApplicable = true,
    onApplicabilityChange,
    domainStatus,
    noEvidenceChecked = false,
    renderExtraContent,
    renderFileBadge,
    isFileMoving,
    isFileProcessing,
    parentVersion,
    preloadedDocumentCount,
    parentDomainScorePercentage,
    onDisabledAreaClick,
  }) => {
    const { t } = useTrans();
    const { dir } = useDirection();

    const handleDisabledAreaClick = () => {
      if (disabled && domainStatus === AssessmentDomainStatus.COMPLETED) {
        onDisabledAreaClick?.();
      }
    };

    // Sort fields by orderIndex
    const sortedFields = [...fields].sort((a, b) => a.orderIndex - b.orderIndex);

    const isDomainInProgress = domainStatus === AssessmentDomainStatus.IN_PROGRESS;
    const isActionsDisabled = disabled || noEvidenceChecked;
    const isApplicabilityDisabled = !isDomainInProgress || isRequired;

    const handleApplicabilityChange = (value: boolean) => {
      if (isApplicabilityDisabled) return;
      onApplicabilityChange?.(value);
    };

    return (
      <NoFlipBox
        mode={dir}
        onClick={handleDisabledAreaClick}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
        }}
      >
        {/* Badge - Only show for optional domains (not required) */}
        {isRequired && (
          <NoFlipBox mode={dir}>
            <Badge
              variant="Required"
              label={<Trans i18nKey={'assessments.new.progressFlow.required'} />}
            />
          </NoFlipBox>
        )}
        {!isRequired && (
          <NoFlipBox mode={dir}>
            <Badge
              variant="Optional"
              label={<Trans i18nKey={'assessments.new.progressFlow.optional'} />}
            />
          </NoFlipBox>
        )}

        {/* Domain Title with optional tooltip (only for optional domains) */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <Typography
            variant="textLG_medium"
            sx={{
              color: 'text.primary',
            }}
          >
            {domainName}
          </Typography>
        </Box>

        {/* Description */}
        {description && (
          <NoFlipBox mode={dir}>
            <Typography
              variant="textSM_regular"
              sx={{
                color: 'black.200',
              }}
            >
              {description}
            </Typography>
          </NoFlipBox>
        )}

        {/* Re-assessment context banner (only when editing v > 1) */}
        {parentVersion != null && preloadedDocumentCount != null && (
          <ReassessmentDomainBanner
            parentVersion={parentVersion}
            preloadedDocumentCount={preloadedDocumentCount}
            parentDomainScorePercentage={parentDomainScorePercentage ?? null}
          />
        )}

        {/* Expected Documents Dropdown */}
        <ExpectedDocumentsDropdown documents={expectedDocuments} />

        {/* Applicability Toggle - Only for Optional Domains */}
        {!isRequired && (
          <Box sx={{ pb: 2 }}>
            <Divider component="div" sx={{ my: 2, borderColor: 'grey.400' }} />
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1.5,
              }}
            >
              <NoFlipTypography variant="textMD_medium" mode={dir}>
                <Trans i18nKey={'assessment.workflow.domain.domainApplicabilityTitle'} />
                <Tip title={t('assessment.workflow.domain.applicabilityHelper')} />
              </NoFlipTypography>

              <NoFlipTypography
                variant="textSM_regular"
                mode={dir}
                sx={{
                  color: 'black.200',
                }}
              >
                <Trans i18nKey={'assessment.workflow.domain.applicabilityQuestion'} />
              </NoFlipTypography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {/* Yes Toggle */}
                <Box
                  onClick={() => handleApplicabilityChange(true)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    cursor: isApplicabilityDisabled ? 'default' : 'pointer',
                  }}
                >
                  <Switch
                    checked={isApplicable}
                    onChange={() => handleApplicabilityChange(true)}
                    disabled={isApplicabilityDisabled}
                    color="primary"
                  />
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.primary',
                      fontWeight: 500,
                      fontSize: '14px',
                    }}
                  >
                    <Trans
                      i18nKey={'assessment.workflow.domain.applicabilityYesLabel'}
                      defaults="Yes, it applies (I will complete this domain and upload the required evidence.)"
                    />
                  </Typography>
                  <Tip title={t('assessment.workflow.domain.applicabilityYesTooltip')} />
                </Box>

                {/* No Toggle */}
                <Box
                  onClick={() => handleApplicabilityChange(false)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    cursor: isApplicabilityDisabled ? 'default' : 'pointer',
                  }}
                >
                  <Switch
                    checked={!isApplicable}
                    onChange={() => handleApplicabilityChange(false)}
                    disabled={isApplicabilityDisabled}
                    color="error"
                  />
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.primary',
                      fontWeight: 500,
                      fontSize: '14px',
                    }}
                  >
                    <Trans
                      i18nKey={'assessment.workflow.domain.applicabilityNoLabel'}
                      defaults="No, not applicable (This domain does not apply to my organization. It will be excluded from scoring.)"
                    />
                  </Typography>
                  <Tip title={t('assessment.workflow.domain.applicabilityNoTooltip')} />
                </Box>
              </Box>
            </Box>
          </Box>
        )}

        {/* Fields Section */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            maxHeight: '90%',
          }}
        >
          {sortedFields.length > 0 &&
            sortedFields.map((field) => (
              <Box
                key={field.id}
                sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, flex: 1, minHeight: 0 }}
              >
                <NoFlipTypography variant="textSM_medium" mode={dir} sx={{ color: 'text.primary' }}>
                  <Trans i18nKey={'assessment.workflow.domain.evidenceFiles'} />
                </NoFlipTypography>
                <DomainFieldRenderer
                  field={field}
                  files={files}
                  onAddFiles={onAddFiles}
                  onDeleteFile={onDeleteFile}
                  onCancelUpload={onCancelUpload}
                  onUndoDelete={onUndoDelete}
                  onRetryDelete={onRetryDelete}
                  onRetryUpload={onRetryUpload}
                  onRemoveFailedUpload={onRemoveFailedUpload}
                  disabled={isActionsDisabled}
                  renderExtraContent={renderExtraContent}
                  renderFileBadge={renderFileBadge}
                  isFileMoving={isFileMoving}
                  isFileProcessing={isFileProcessing}
                />
              </Box>
            ))}
        </Box>
      </NoFlipBox>
    );
  },
);
